import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import { Locale as LocaleString } from "@shared/types";
import { isDate } from "date-fns";
import { de, enUS as en, es, Locale } from "date-fns/locale";

import german from "./locales/de/translation.json";
import english from "./locales/en/translation.json";
import spanish from "./locales/es/translation.json";
import { DateResource, formatIntlDate } from "./locales/utils/formatIntlDate";
import { formatIntlNumber } from "./locales/utils/formatIntlNumber";
import { formatIntlWeight } from "./locales/utils/formatIntlWeight";

interface Resources {
  [key: string]: {
    translation: {
      [key: string]: string | object;
    };
  };
}

export const resources: Resources = {
  en: { translation: english },
  es: { translation: spanish },
  de: { translation: german },
};

interface FallbackLng {
  [key: string]: [string];
}
const fallbackLng: FallbackLng = {
  de_DE: ["de"],
  en_US: ["en"],
  en_GB: ["en"],
  en_AU: ["en"],
  en_DE: ["en"],
  default: ["en"],
};

interface Locales {
  [key: string]: Locale;
}

const locales: Locales = { en, es, de };
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng,
    debug: false,
    defaultNS: "translation",
    ns: ["translation"],
    returnNull: false,
    interpolation: {
      format: (value, format, lng) => {
        if (!lng || !format) return;
        if (value && (isDate(value) || isDate(value.start))) {
          const locale = getLocale(locales, lng, fallbackLng);
          if (!locale) return value;
          const resource = getResource(resources, lng, fallbackLng);
          return formatIntlDate(value, format, locale, resource);
        } else if (format.includes("number")) {
          const locale = getLocale(locales, lng, fallbackLng);
          if (!locale) return value;
          return formatIntlNumber(value ?? 0, locale);
        } else if (format.includes("weight")) {
          const locale = getLocale(locales, lng, fallbackLng);
          if (!locale) return value;
          const resource = getResource(resources, lng, fallbackLng);
          return formatIntlWeight(value ?? 0, format, locale, resource, lng as LocaleString);
        }
        return value;
      },
      escapeValue: false,
    },
  });

export { default as i18next } from "i18next";
export default i18n;

function getLocale(locales: Locales, lng: string, fallbackLng: FallbackLng) {
  return locales[lng] ?? (fallbackLng[lng] && locales[fallbackLng[lng][0]]) ?? locales["en"];
}
function getResource(resources: Resources, lng: string, fallbackLng: FallbackLng) {
  return (resources[lng] ?? (fallbackLng[lng] && resources[fallbackLng[lng][0]]) ?? resources["en"]) as DateResource;
}
